import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { TranslateModule } from '@ngx-translate/core';
import { ExportJobCardComponent, ExportJobCardState } from '../export-job-card/export-job-card.component';

@Component({
  selector: 'xemex-export-panel',
  standalone: true,
  imports: [
    CommonModule,
    ScrollingModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    TranslateModule,
    ExportJobCardComponent,
  ],
  templateUrl: './export-panel.component.html',
  styleUrl: './export-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportPanelComponent {
  // Inputs
  activeJobs = input.required<ExportJobCardState[]>();

  // State
  protected readonly isExpanded = signal(false);

  // Computed
  protected readonly activeCount = computed(() => this.activeJobs().length);
  protected readonly isPollingActive = computed(() => this.activeJobs().length > 0);

  constructor() {
    // Auto-expand when a job becomes active. Polling itself — and updating `activeJobs` — is
    // owned entirely by ExportJobStore; this panel is a pure display of that state.
    effect(() => {
      if (this.activeJobs().length > 0) {
        this.isExpanded.set(true);
      }
    });
  }

  protected toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  /** Mirrors ExportJobStore's own interval selection, purely for the "refreshing every Xs" label. */
  protected getPollIntervalDisplay(): string {
    const jobs = this.activeJobs();
    if (jobs.some(j => j.status === 'running')) return '2s';
    if (jobs.some(j => j.status === 'queued')) return '10s';
    return '30s';
  }
}
